<template>
  <section class="gallery-panel">
    <div class="gallery-toolbar">
      <span class="p-input-icon-left search-box">
        <i class="pi pi-search" />
        <InputText
          v-model="search"
          :placeholder="type === 'equipment' ? 'Nome, série ou modelo' : 'Nome, referência ou descrição'"
          @input="scheduleSearch"
        />
      </span>
      <span class="gallery-count">{{ total }} {{ total === 1 ? "imagem" : "imagens" }}</span>
    </div>

    <div v-if="loading" class="gallery-state">
      <i class="pi pi-spin pi-spinner"></i> A carregar imagens...
    </div>
    <div v-else-if="!images.length" class="gallery-state empty">
      <i class="pi pi-images"></i>
      <strong>Nenhuma imagem encontrada</strong>
      <span>Importe imagens ou altere a pesquisa.</span>
    </div>
    <div v-else class="image-grid">
      <article v-for="image in images" :key="image.id" class="image-card">
        <div class="image-stage">
          <AuthenticatedImage :file="image" :alt="image.file_origin_name" height="180" />
          <span class="position-badge">{{ positionLabel(image) }}</span>
          <span class="status-dot" :class="image.api_status ? 'published' : 'local'">
            {{ image.api_status ? "Publicada" : "Local" }}
          </span>
        </div>
        <div class="image-content">
          <div class="file-heading">
            <div>
              <strong :title="image.file_origin_name">{{ image.file_origin_name || image.file_name }}</strong>
              <small>#{{ image.id }} · {{ formatDate(image.created_at) }}</small>
            </div>
            <Button
              v-if="can('createUpdateParts')"
              icon="pi pi-trash"
              class="p-button-rounded p-button-text p-button-danger"
              v-tooltip.top="'Eliminar imagem'"
              @click="$emit('delete', image)"
            />
          </div>
          <div class="entity-list">
            <div v-for="entity in image.entities" :key="entity.id" class="entity-row">
              <div>
                <span class="entity-key">{{ entity.equipment_series || entity.reference }}</span>
                <span class="entity-name">{{ entity.model_name || entity.description || "Sem descrição" }}</span>
              </div>
              <span v-if="entity.brand || entity.family" class="entity-context">
                {{ entity.brand ? entity.brand.name : entity.family.name }}
              </span>
            </div>
          </div>
          <small v-if="image.entity_count > 1" class="shared-note">
            <i class="pi pi-link"></i> Partilhada por {{ image.entity_count }} equipamentos
          </small>
        </div>
      </article>
    </div>

    <Paginator
      v-if="total > perPage"
      :first="(page - 1) * perPage"
      :rows="perPage"
      :totalRecords="total"
      :rowsPerPageOptions="[12, 24, 48, 96]"
      template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
      currentPageReportTemplate="{first} a {last} de {totalRecords}"
      @page="changePage"
    />
  </section>
</template>

<script>
import AuthenticatedImage from "@/components/elements/AuthenticatedImage.vue";
import sparsService from "@/services/spars.service.js";

export default {
  name: "ImageGalleryPanel",
  components: { AuthenticatedImage },
  props: {
    type: { type: String, required: true },
    active: { type: Boolean, default: false },
    refreshToken: { type: Number, default: 0 },
  },
  data() {
    return { images: [], loading: false, page: 1, perPage: 24, total: 0, search: "", searchTimer: null, loaded: false };
  },
  watch: {
    active: {
      immediate: true,
      handler(value) { if (value && !this.loaded) this.load(); },
    },
    refreshToken() { if (this.active) this.load(); },
  },
  beforeDestroy() { clearTimeout(this.searchTimer); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const response = await sparsService.getGalleryImages(this.type, {
          page: this.page,
          per_page: this.perPage,
          search: this.search || undefined,
        });
        this.images = response.data || [];
        this.total = Number(response.total || 0);
        this.page = Number(response.current_page || 1);
        this.loaded = true;
      } catch (error) {
        this.$toast.add({ severity: "error", summary: "Erro ao carregar imagens", life: 4000 });
      } finally {
        this.loading = false;
      }
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.load(); }, 350);
    },
    changePage(event) {
      this.page = event.page + 1;
      this.perPage = event.rows;
      this.load();
    },
    positionLabel(image) {
      const relation = image.entities[0];
      if (!relation) return "Sem associação";
      return relation.is_primary ? "Principal" : `Posição ${relation.position + 1}`;
    },
    formatDate(value) {
      return value ? new Intl.DateTimeFormat("pt-PT").format(new Date(value)) : "Sem data";
    },
  },
};
</script>

<style scoped>
.gallery-panel { padding-top: 1rem; }
.gallery-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.search-box { width: min(32rem, 100%); }
.search-box input { width: 100%; }
.gallery-count { color: #687a84; font-size: .85rem; font-weight: 600; white-space: nowrap; }
.image-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(270px, 1fr)); gap: 1rem; }
.image-card { overflow: hidden; border: 1px solid #dbe5e9; border-radius: 12px; background: #fff; box-shadow: 0 4px 14px rgba(25, 55, 68, .06); }
.image-stage { position: relative; display: flex; align-items: center; justify-content: center; height: 210px; background: linear-gradient(145deg, #edf3f5, #f8fafb); }
.image-stage ::v-deep .p-image img { max-width: 100%; max-height: 180px; object-fit: contain; }
.position-badge, .status-dot { position: absolute; top: .7rem; padding: .3rem .55rem; border-radius: 999px; font-size: .68rem; font-weight: 700; }
.position-badge { left: .7rem; background: #163746; color: #fff; }
.status-dot { right: .7rem; }
.status-dot.published { background: #d9f2e7; color: #147653; }
.status-dot.local { background: #fff0cd; color: #8c6100; }
.image-content { padding: .9rem; }
.file-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: .5rem; }
.file-heading strong { display: block; max-width: 210px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #1d3642; }
.file-heading small { display: block; margin-top: .2rem; color: #83929a; }
.entity-list { margin-top: .8rem; border-top: 1px solid #edf1f3; }
.entity-row { display: flex; justify-content: space-between; gap: .75rem; padding: .6rem 0; border-bottom: 1px solid #edf1f3; }
.entity-key, .entity-name { display: block; }
.entity-key { font-weight: 700; color: #087f8c; }
.entity-name { margin-top: .15rem; color: #526772; font-size: .78rem; }
.entity-context { align-self: center; color: #73848c; font-size: .72rem; text-align: right; }
.shared-note { display: block; margin-top: .65rem; color: #627780; }
.gallery-state { display: flex; align-items: center; justify-content: center; gap: .6rem; min-height: 18rem; color: #657984; }
.gallery-state.empty { flex-direction: column; }
.gallery-state.empty > i { color: #9eb6bf; font-size: 2.4rem; }
@media (max-width: 640px) {
  .gallery-toolbar { align-items: stretch; flex-direction: column; }
  .image-grid { grid-template-columns: 1fr; }
}
</style>
